import React from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../../components/breadcrumbs/Breadcrumbs";
import _image_service from "../../../../../../../services/ImageService";
import '../../../../../../styles/Common.scss';
import '../../../WorldPages.scss';
import '../MagicItemsPage.scss';

interface MagicWardPageProps {
}

const MagicWardPage = (props: MagicWardPageProps) => {

    const page: PageActions = new PageActions({
        title: `Casarok: Magic Wards - Chase the Fox`,
        description: "Wards are magic items that act as deterrents for monsters to keep them from populated areas of Casarok!",
        keywords: ["casarok", "magic", "magic compendium", "magic item", "magical", "magical item", "magic ward", "monster ward"],
        imgSrc: `chase/world/magic/items/ward.png`,
        url: `world/magic/items/ward`
    });

    return page.generateMetaContent(
        <>
            <ContentBox header="Wards">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <img className="magic-item-pic right"
                         src={_image_service.getCdnImageSrc(`chase/world/magic/items/ward.png`)}
                         alt="A strcture, similar to a communications tower, with a red orb atop it radiating red energy"
                         title="A modern ward, usually built as a tower utilizing mage-tech"/>
                    <p>
                        Magical wards are conduit items that act as repellents and deterrents
                        against <NavLink className="world-link" to="/world/bestiary/monster">monster</NavLink> activity.
                        Wards are usually installed in places of interest to protect anthros from such threats.
                        Population centers are the most common to defend those who are incapable of doing so themselves.
                        Economic hubs, national monuments, and utility stations are also places in which wards are
                        employed. Travellers can use a mobile ward to minimize their risks while on the road. The spell
                        used to create a ward is dependent on the type of monsters or
                        the <NavLink className="world-link" to="/world/magic/elements">elemental affiliation</NavLink> of
                        the monsters that are to be deterred. A generic enchantment also exists that keeps away all
                        monsters; however, it is either only good for weak threats, extremely costly in terms of mana,
                        runs out of power quickly, or has a combination of downsides.
                    </p>
                    <p>
                        Wards work by enchanting a transmission medium with the warding that meets the requirements for
                        the monsters that need to be repelled. The transmitter will then send the ward out in pulses
                        akin to sound waves. The type of spell is usually of the element that is most effective against
                        a type of monster. The type of ward can also be determined by the magical energy or glow it
                        emits. For example, a ward that defends
                        against <NavLink className="world-link" to="/world/bestiary/monster/frost-jotnr">frost jotnrs</NavLink> will
                        be of the <NavLink className="world-link" to="/world/magic/elements/fire">fire</NavLink> element
                        and give off a warm, bright red glow. The effectiveness of a ward is based on the amount of mana
                        used to create the ward, the range of its protection, and the threat level of the monsters it
                        deters.
                    </p>
                    <p>
                        Wards have been utilized throughout most of Casarok's history. They were invented sometime in
                        the <NavLink className="world-link" to="/world/history">pre-Cataclysmic</NavLink> era of the
                        world. At that time it was merely a spell that could be cast to create a repelling force
                        against weak monsters. The spell was improved over time because of its potential to protect
                        villages and trade caravans until it eventually became an enchantment that could be applied in
                        the manner of a conduit. Once the conduit was established for the spell, wards were essentially
                        always magical items -- so much so that almost all anthros in post-Cataclysmic Casarok will
                        equate wards as magical items and never as a spell that can be cast.
                    </p>
                    <p>
                        In the <NavLink className="world-link" to="/world/history/modern-age">modern day</NavLink>,
                        wards have taken on very different forms. Most cities and towns will have them installed as a
                        tower, not unlike a radio or cell tower. A ward tower needs to be serviced and recharged
                        periodically, and this service is provided by a utility company. Weak or activatable wards are
                        also installed as a chip in most smartphones. Smartphone wards also need to be recharged from
                        time to time, but this process can be done through general phone recharging. The effect in a
                        smartphone can be amplified by a ward tower in the vicinity if both are compatible with this
                        functionality, but this effect is rare as it requires the cell tower and ward tower to be one
                        in the same or magically linked. Thus, only cities with the money to fund such an apparatus are
                        capable of building them. Both of these ward technologies rely
                        on <NavLink className="world-link" to="/world/magic/items/mage-tech">mage-tech</NavLink> to
                        function.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/bestiary/floradon">Floradon</NavLink> wards also
                        exist, but they are often not used except in areas where they live. Not only is the floradon
                        ward enchantment more complex due to the reliance on multiple elemental types but they also
                        are not really necessary as powerful floradons can only be found in deep forests and wild areas.
                        The lower threat ones are no more problematic than some of the weak critters
                        like <NavLink className="world-link" to="/world/bestiary/feral/oscrobon">oscrobons</NavLink>, so
                        a ward is not necessary in such cases.
                    </p>
                </div>
            </ContentBox>
        </>
    );
}

export default MagicWardPage;